import type { GetVideoProblemCode, VideoReference } from 'src/generated/graphql-manager';

import { IconButton, LinkIcon, Note, PlayerIcon, Tooltip, TrashIcon } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import styles from './event-cover.module.css';

type Props = {
  coverVideoId?: VideoReference['videoId'] | null;
  videoError: GetVideoProblemCode;
  disabled?: boolean;
  testIdPrefix?: string;
  onDelete?: () => void;
  onLinkToVmpro?: () => void;
};

export function EventCoverError({ videoError, disabled, testIdPrefix, coverVideoId, onDelete, onLinkToVmpro }: Props) {
  const { t } = useTranslation();
  const unauthorizedAccess = videoError === 'UNAUTHORIZED_ACCESS';
  const vmproLinkMissing = videoError === 'VMPRO_LINK_MISSING';

  return (
    <div data-testid={`${testIdPrefix}cover`} className={styles.eventCoverError}>
      <Note
        variant="primary"
        className={styles.infoBox}
        data-testid={`${testIdPrefix}cover-video-error`}
        title={
          unauthorizedAccess
            ? t('manager.webcastSetup.media.errors.videoPermissionFailed')
            : t('manager.webcastSetup.media.errors.vmproLinkMissing')
        }
      >
        <span className={styles.videoId}>{t('manager.webcastSetup.media.errors.videoId')}</span>
        <span data-testid={`${testIdPrefix}cover-video-id`}>{coverVideoId}</span>
      </Note>

      <footer className={styles.footer}>
        <div className={styles.title}>
          <span data-testid={`${testIdPrefix}cover-label`}>
            <PlayerIcon />
          </span>
        </div>

        {unauthorizedAccess && (
          <Tooltip label={t('manager.webcastSetup.media.deleteCoverVideo')}>
            <IconButton
              data-testid={`${testIdPrefix}cover-delete-button`}
              rounded
              type="button"
              disabled={disabled}
              onClick={onDelete}
              aria-label={t('manager.webcastSetup.media.deleteCoverVideo')}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        )}

        {vmproLinkMissing && (
          <Tooltip label={t('manager.webcastSetup.media.linkToVmpro')}>
            <IconButton
              data-testid={`${testIdPrefix}cover-link-button`}
              rounded
              type="button"
              disabled={disabled}
              onClick={onLinkToVmpro}
              aria-label={t('manager.webcastSetup.media.linkToVmpro')}
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>
        )}
      </footer>
    </div>
  );
}
